import { Card, CardContent, Stack, Typography } from "@mui/material";

const STEPS = [
  <>
    Instale o aplicativo <strong>Fluxhealth</strong> no seu smartphone e faça
    login com a mesma conta utilizada no Fluxmed.
  </>,
  <>
    No aplicativo <strong>Fluxhealth</strong>, ative as permissões de acesso aos
    dados do <strong>Samsung Health</strong> (passos, sono, frequência cardíaca,
    etc.).
  </>,
  <>
    Envie seus dados pelo <strong>Fluxhealth</strong> e volte a esta tela para
    acompanhar as medições do período.
  </>,
];

interface WearableEmptyStateProps {
  /** 1 = dia único; demais = intervalo (semana, quinzena, mês). */
  rangeDays?: number;
  /**
   * patient = orientação de instalação (tela do paciente);
   * professional = mensagem curta no atendimento, sem passos de setup.
   */
  audience?: "patient" | "professional";
}

/** Estado vazio: paciente vê como instalar; médico só vê que não há dados. */
export function WearableEmptyState({
  rangeDays = 1,
  audience = "patient",
}: WearableEmptyStateProps) {
  const isSingleDay = rangeDays === 1;
  const isProfessional = audience === "professional";

  return (
    <Stack spacing={3} alignItems="center" sx={{ py: 3, width: "100%" }}>
      <Stack spacing={0.75} alignItems="center" sx={{ textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {isSingleDay
            ? isProfessional
              ? "O paciente não possui dados no dia selecionado"
              : "Nenhum dado encontrado para o dia selecionado"
            : isProfessional
              ? "O paciente não possui dados no período selecionado"
              : "Nenhum dado encontrado para o período selecionado"}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {isProfessional
            ? isSingleDay
              ? "Não há medições enviadas pelo FluxHealth nesta data."
              : "Não há medições enviadas pelo FluxHealth neste intervalo."
            : isSingleDay
              ? "Você ainda não possui registros de dispositivos conectados nesta data."
              : "Você ainda não possui registros de dispositivos conectados neste intervalo."}
        </Typography>
      </Stack>

      {/* Passos de instalação só fazem sentido na tela do paciente */}
      {!isProfessional && (
        <Card
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 640,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "primary100.main",
            backgroundColor: "primary50.main",
          }}
        >
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Stack spacing={2}>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Para visualizar seus dados aqui, é necessário ter um relógio
                inteligente conectado ao <strong>Samsung Health</strong> e
                seguir os passos abaixo:
              </Typography>

              <Stack spacing={1.75}>
                {STEPS.map((text, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                  >
                    <Typography
                      sx={{
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: "primary100.main",
                        color: "primary800.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Typography variant="body1">{text}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
